// Admin Team — list existing admins, search & promote users to admin, or demote back.
import { useEffect, useMemo, useState } from "react";
import api, { extractError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  X, ShieldCheck, User, UserPlus, UserMinus, Search, Loader2, Crown,
  RefreshCw, AlertTriangle,
} from "lucide-react";

const ROLE_LABEL = { customer: "زبون", driver: "سائق", restaurant: "مطعم", admin: "مشرف" };

export default function AdminTeamModal({ onClose }) {
  const { user: me } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/team");
      setAdmins(data.admins || []);
    } catch (e) { setErr(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Debounced search
  useEffect(() => {
    if (q.trim().length < 3) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/admin/team/search?q=${encodeURIComponent(q.trim())}`);
        // Exclude current admins from results
        const admIds = new Set(admins.map((a) => a.id));
        setResults((data.users || []).filter((u) => !admIds.has(u.id)));
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, admins]);

  const promote = async (u) => {
    if (!window.confirm(`ترقية ${u.name || u.phone || u.email} إلى مشرف؟ سيحصل على صلاحيات كاملة على النظام.`)) return;
    setBusyId(u.id); setErr(""); setOk("");
    try {
      await api.post("/admin/team/promote", { user_id: u.id });
      setOk(`تمت ترقية ${u.name || u.phone} إلى مشرف`);
      setTimeout(() => setOk(""), 2500);
      setQ(""); setResults([]);
      await load();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusyId(null); }
  };

  const demote = async (u) => {
    if (!window.confirm(`إزالة صلاحيات المشرف عن ${u.name || u.phone}؟ سيعود إلى دور زبون.`)) return;
    setBusyId(u.id); setErr(""); setOk("");
    try {
      await api.post("/admin/team/demote", { user_id: u.id });
      setOk(`تم إزالة صلاحيات ${u.name || u.phone}`);
      setTimeout(() => setOk(""), 2500);
      await load();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusyId(null); }
  };

  const canDemote = admins.length > 1;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
         onClick={onClose} data-testid="admin-team-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white w-full sm:max-w-2xl rounded-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 flex items-center justify-between text-white"
             style={{background: "linear-gradient(135deg, #1E3A8A 0%, #2547D0 100%)"}}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center">
              <Crown size={22}/>
            </div>
            <div>
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>فريق الإدارة</div>
              <div className="text-xs opacity-90">{admins.length} مشرف على النظام</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center" title="تحديث"
                    data-testid="team-refresh">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""}/>
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center"
                    data-testid="team-close">
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {ok && <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-2 rounded-lg text-sm" data-testid="team-ok">{ok}</div>}
          {err && <div className="bg-red-50 text-red-600 border border-red-200 px-3 py-2 rounded-lg text-sm" data-testid="team-err">{err}</div>}

          {/* Promote user */}
          <section>
            <h3 className="font-black text-sm flex items-center gap-2 mb-3" style={{fontFamily:"Cairo"}}>
              <UserPlus size={14}/> إضافة مشرف جديد
            </h3>
            <div className="relative">
              <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"/>
              <input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="ابحث برقم الهاتف أو البريد أو الاسم..."
                     className="w-full pr-10 pl-3 py-2.5 rounded-xl border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none text-sm"
                     data-testid="team-search"/>
              {searching && <Loader2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 animate-spin text-primary-brand"/>}
            </div>
            {q.trim().length > 0 && q.trim().length < 3 && (
              <div className="text-[11px] text-[var(--text-secondary)] mt-1">اكتب 3 أحرف على الأقل</div>
            )}

            {results.length > 0 && (
              <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto">
                {results.map((u) => (
                  <div key={u.id} className="flex items-center gap-2 bg-[var(--surface-alt)] rounded-xl px-3 py-2"
                       data-testid={`team-search-${u.id}`}>
                    <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center font-black" style={{fontFamily:"Cairo"}}>
                      {(u.name || u.phone || "?")[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="font-black text-sm truncate">{u.name || "بدون اسم"}</div>
                        <span className="text-[10px] font-bold text-[var(--text-secondary)] bg-white px-1.5 py-0.5 rounded">{ROLE_LABEL[u.role] || u.role}</span>
                        {u.active === false && <span className="text-[10px] font-bold text-red-500">معطّل</span>}
                      </div>
                      <div className="text-[11px] text-[var(--text-secondary)] truncate" dir="ltr">
                        {u.phone || u.email || u.id}
                      </div>
                    </div>
                    <button onClick={() => promote(u)} disabled={busyId === u.id}
                            className="px-3 py-1.5 rounded-lg bg-primary-brand text-white text-xs font-black hover:opacity-90 disabled:opacity-60 flex items-center gap-1"
                            data-testid={`team-promote-${u.id}`}>
                      <UserPlus size={12}/> ترقية
                    </button>
                  </div>
                ))}
              </div>
            )}
            {q.trim().length >= 3 && !searching && results.length === 0 && (
              <div className="mt-3 text-center py-4 text-xs text-[var(--text-secondary)]">لا نتائج مطابقة</div>
            )}
          </section>

          {/* Current admins */}
          <section className="pt-4 border-t border-[var(--border)]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-black text-sm flex items-center gap-2" style={{fontFamily:"Cairo"}}>
                <ShieldCheck size={14}/> المشرفون الحاليون
              </h3>
              <span className="text-xs text-[var(--text-secondary)] font-bold">{admins.length} مشرف</span>
            </div>
            {loading ? (
              <div className="py-6 text-center text-sm text-[var(--text-secondary)]">جاري التحميل...</div>
            ) : (
              <div className="space-y-2">
                {admins.map((a) => {
                  const isMe = a.id === me?.id;
                  return (
                    <div key={a.id}
                         className={`flex items-center gap-2 rounded-xl px-3 py-2 border ${isMe ? "bg-primary-light border-primary-brand/30" : "bg-white border-[var(--border)]"}`}
                         data-testid={`team-admin-${a.id}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-white ${isMe ? "bg-primary-brand" : "bg-[var(--secondary)]"}`} style={{fontFamily:"Cairo"}}>
                        {(a.name || "?")[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="font-black text-sm truncate">{a.name || "بدون اسم"}</div>
                          {isMe && <span className="text-[10px] font-black bg-primary-brand text-white px-1.5 py-0.5 rounded">أنت</span>}
                          <Crown size={11} className="text-primary-brand"/>
                        </div>
                        <div className="text-[11px] text-[var(--text-secondary)] truncate" dir="ltr">{a.email || a.phone || a.id}</div>
                      </div>
                      {!isMe && (
                        <button onClick={() => demote(a)}
                                disabled={busyId === a.id || !canDemote}
                                title={canDemote ? "" : "لا يمكن إزالة آخر مشرف في النظام"}
                                className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 text-xs font-black disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                                data-testid={`team-demote-${a.id}`}>
                          <UserMinus size={12}/> إزالة
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Warning */}
          <div className="text-[11px] text-[var(--text-secondary)] bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5"/>
            <div>
              <b>تنبيه</b>: المشرف يمتلك صلاحيات كاملة على النظام (رؤية جميع البيانات، تعديل الأسعار، دفع المكافآت، الوصول إلى محافظ السائقين). امنح الصلاحية لأشخاص موثوقين فقط.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
